import { HEADING_POSITIONS } from "./CustomerCommunication";
import { DealerLocationID } from "./DealerLocation";
import { LANGUAGE_CODES } from "./Language";
import { ReportTemplateID } from "./ReportTemplate";

export type CommunicationSettingID = number;

export enum COMMUNICATION_LAYOUTS {
  Layout1 = 1,
  Layout2 = 2
}

export interface CommunicationLayout {
  id: COMMUNICATION_LAYOUTS;
  description: string;
}

export interface CommunicationSetting {
  communication_detail_id: CommunicationSettingID;
  color: string; // Color
  logo: string; // Logo
  welcome_text: string; // Welcome text
  welcome_picture: string; // Welcome picture
  success_text: string; // Success text
  repair_overview_welcome_text: string; // Car Ready Welcome text
  check_in_welcome_text: string; // Checkin Welcome text
  check_in_success_text: string; // Checkin Success text
  layout: COMMUNICATION_LAYOUTS; // Layout
  diagnose_overview_remarks_enabled: boolean; // Is Diagnose overview remarks enabled
  checkin_remarks_enabled: boolean; // is checkin remarks enabled
  phone_number: string; // Phone number
  email: string; // Email
  dealer_location_id: DealerLocationID; // Dealer Location Identifier
  support_enabled: boolean; // Is support enabled
  support_text: string; // Support text
  check_in_days_prior: number; // Number of days before which online checkin can be send
  online_check_in_send_pin: boolean; // Include PIN in online checkin
  send_parking_gate_code: boolean; // Sending parking gate code is enabled
  online_check_in_name_visible: boolean; // Name visible in online checkin
  diagnose_overview_name_visible: boolean; // Name visible in diagnose overview
  check_in_testing_mode: boolean; // Is testing mode for online checkin
  check_in_testing_email: string; // Testing email for online checkin
  check_in_testing_phone: string; // Testing phone for online checkin
  counter_tablet_home_url: string; // Home URL for the counter tablet
  diagnose_cc_testing_mode: boolean; // Is testing mode enabled for diagnose communication
  diagnose_cc_testing_email: string; // Testing email for diagnose communication
  diagnose_cc_testing_phone: string;
  repair_cc_testing_mode: boolean; // Is testing mode enabled for repair communication
  repair_cc_testing_email: string; // Testing email for repair communication
  repair_cc_testing_phone: string; // Testing phone for repair communication
  automatically_send_repair_overview_when_car_ready: boolean; // On setting a workorder in car ready state, automatically send repair overview
  automatically_send_repair_overview_when_quality_check: boolean; // On performing quality check on a workorder, automatically send repair overview
  automatically_send_repair_overview_on_car_ready_and_billed: boolean; // Automatically send a repair overview whenever car is ready and workorder is billed.
  automatically_send_repair_overview_on_car_ready_and_out_of_shop: boolean; // Automatically send a repair overview whenever car is ready and moved out of shop.
  disable_sending_automated_repair_overview_on_quality_issues: boolean; // Disable sending automated repair overview on quality issues, i.e. if enabled, repair overview will not be sent automatically on the quality check.
  hide_price_for_automatic_repair_order: boolean; // When a repair overview is sent automatically this will hide the prices for the customer
  website: string; // Website
  report_template_id: ReportTemplateID; // Report template identifier
  heading_position: HEADING_POSITIONS; // Heading position
  send_confirmation_emails: boolean; // Can send confirmation emails
  enable_not_fixed_advised: boolean; // Is do not fix advised items enabled
  enable_not_fixed_critical: boolean; // Is do not fix critical items enabled
  cannot_decline_warranty_types: boolean; // Cannot decline interventions/question results which are pinned with warranty type
  enable_not_fix_without_status: boolean; // Is do not fix without status items enabled, applicable to interventions which are not converted/connected to a check question result
  skip_without_status_items: boolean; // Is skipping without status items enabled, applicable to interventions which are not converted/connected to a check question result
  skip_optional_items: boolean; // Is skipping optional items enabled
  hide_lease_intervention_price: boolean; // Hide price for interventions of lease appointment
  hide_internal_intervention_price: boolean; // Hide price for internal intervention
  hide_warranty_intervention_price: boolean; // Hide price for warranty type intervention
  hide_recall_intervention_price: boolean; // Hide price for recall type intervention
  hide_prices_in_desk_communication: boolean; // Hide prices in desk communication. Used when the location always expects the driver of the vehicle to not be the owner
  block_automated_communication_for_lease_customer: boolean; // Block the automate communication for lease customers
  is_sms_for_automated_customer_communication_enabled: boolean; // Is SMS enabled for the automated customer communication
  is_bird_whats_app_for_automated_customer_communication_enabled: boolean; // Is Bird WhatsApp enabled for the automated customer communication
  is_customer_name_in_kiosk_checkin_enabled: boolean; // Is customer name enabled in kiosk checkin
  is_customer_signature_in_kiosk_checkin_enabled: boolean; // Is customer signature enabled in kiosk checkin
  is_customer_ok_read_only_in_desk_check_in: boolean; // If set to true, the Customer OK option in desk checkin is read only, i.e. Customer cannot set ok for the results
  is_desk_contact_info_enabled: boolean; // Show the customer a popup on desk-checkin for the customer to enter their contact information
  external_js: string; // External JS code snippet
  external_css: string; // External CSS code snippet
  google_analytics_id: string | null; // Google Analytics ID
  display_intervention_elements: boolean; // Display intervention elements
  enable_closing_pop_up_on_cc: boolean; // Enable closing pop up on communications
  show_parking_location_note_on_kiosk_check_in: boolean; // If enabled, then kiosk checkin will show an option to customer to add a parking note, this note is treated as parking note on a workorder.
  is_service_tracker_enabled: boolean; // If true, then customer communication will be displayed via service tracker.
  service_tracker_check_in_message: string; // Custom message to be displayed on service tracker at Check In stage
  service_tracker_check_in_car_diagnostic_message: string; // Custom message to be displayed on service tracker between check in and diagnostic stage
  service_tracker_car_diagnostic_message: string; // Custom message to be displayed on service tracker on car diagnostic stage
  service_tracker_car_diagnostic_waiting_for_approval_message: string; // Custom message to be displayed on service tracker between car diagnostic and waiting for approval stage
  service_tracker_waiting_for_approval_message: string; // Custom message to be displayed on service tracker on waiting for approval stage
  service_tracker_waiting_for_approval_repair_items_message: string; // Custom message to be displayed on service tracker between waiting for approval and repair items stage
  service_tracker_repair_items_message: string; // Custom message to be displayed on service tracker on repair items stage
  service_tracker_final_check_message: string; // Custom message to be displayed on final check stage
  service_tracker_picked_up_car_message: string; // Custom message to be displayed on pick up car stage
  is_star_rating_enabled: boolean; // Is star rating enabled
  star_rating_experience_message: string; // Star rating experience message
  star_rating_service_message: string; // Star rating service message
  star_rating_speed_message: string; // Star rating speed message
  layouts?: CommunicationLayout[]; // List of communication layouts
  language_code?: LANGUAGE_CODES; // Language code
  vat?: number; // VAT
  include_vat?: boolean; // Is VAT Included
  dms_price_enabled?: boolean; // Is DMS price enabled
  total_pdf_enabled?: boolean; // is total pdf enabled
  dealer_name?: string; // Dealer name
  location_name?: string; // Location name
  location_street?: string; // Location street
  location_postal_code?: string; // Location postal code
  location_city?: string; // Location city
  location_country?: string; // Location country
  headline?: string; // Headline
  summer_a_threshold?: number; // A Threshold for Summer tyres
  summer_n_threshold?: number; // N Threshold for Summer tyres
  winter_a_threshold?: number; // A Threshold for Winter tyres
  winter_n_threshold?: number; // N Threshold for Winter tyres
  all_season_a_threshold?: number; // A Threshold for All season tyres
  all_season_n_threshold?: number; // N Threshold for All season tyres
  mechanic_name?: string; // Mechanic name
  show_mechanic_name?: boolean; // Show mechanic name
  is_employee_name_visible?: boolean; // Is employee name visible
  has_sms?: boolean; // Is SMS supported
  has_whats_app?: boolean; // Is WhatsApp supported
  is_acses_enabled?: boolean; // Is Acses Enabled
  maintenance_mode?: boolean; // Is Maintenance Mode enabled
  generate_ai_summary?: boolean; // Generating summary for customer communication is enabled for this location
  counter_tablet_home_url_delay?: number; // Delay in seconds before the counter tablet opens the home URL
}
